import { useEffect, useState, useRef } from 'react';
import { openBase64PdfInTab } from '@consumidor-positivo/ts-utils';
import useApiBillet from '@common/apis/billet/useApiBillet';
import constants from '@common/constants';
import { pageNames } from '@common/routes/pageNames';
import { GenerateBilletResponse } from '@common/apis/billet/types';
import * as caixa from '@dues/helpers/caixa';
import { ClosedAgreementProps } from '@dues/pages/Partner/ClosedAgreement/types';
import useBilletTracking from './useBilletTracking';

interface UseBilletHandlerProps {
  onBilletGenerated?: (billetData: GenerateBilletResponse) => void;
  onBilletErrored?: (err: any) => void;
  refCopiedWarning?: React.RefObject<any>;
  isMyAgreementsPage?: boolean;
  agreement?: any;
  installment?: any;
  closedAgreement?: ClosedAgreementProps;
  msUrl: string;
}

const useBilletHandler = ({
  msUrl,
  refCopiedWarning,
  isMyAgreementsPage = false,
  agreement,
  installment,
  closedAgreement,
}: UseBilletHandlerProps) => {
  const isMounted = useRef(true);
  const isPolling = useRef(false);
  const POLLING_INTERVAL_MS = 2000;
  const MAX_BILLET_POLLING_ATTEMPTS = 5;

  const { apiGenerateBillet } = useApiBillet(msUrl);
  const { trackCodeCopy, trackCodeRetrieved, trackRetrievalFailed, trackBilletDownload, trackBilletFileRetrieved, trackDownloadFailed } =
    useBilletTracking({ agreement, installment, closedAgreement });

  function checkBilletData() {
    if (closedAgreement?.flowWithPix && closedAgreement.singlePaymentMethod && closedAgreement.paymentMethod.id === 'PIX') {
      return {};
    }

    if (!!closedAgreement?.billetData?.linhaDigitavel) {
      return closedAgreement?.billetData;
    }

    const billetRequest = localStorage.getItem(constants.debts.BILLET_REQ);
    const isBilletPending = billetRequest === 'pending';
    const foundBilletData = billetRequest && !isBilletPending ? JSON.parse(billetRequest)?.linhaDigitavel : false;
    if (foundBilletData) {
      return foundBilletData;
    }

    return null;
  }

  function checkIsPollingBillet() {
    const billetData = checkBilletData();

    if (billetData === null) return true;
    return false;
  }

  const [billet, setBillet] = useState<GenerateBilletResponse | null>(checkBilletData());
  const [isPollingBillet, setIsPollingBillet] = useState<boolean>(checkIsPollingBillet());
  const [billetBtnTxt, setBilletBtnTxt] = useState<string>('Copiar código do boleto');
  const [isBilletGenerationError, setIsBilletGenerationError] = useState<boolean>(false);
  const [loadingDownloadBillet, setLoadingDownloadBillet] = useState<boolean>(false);

  interface NewGenerateBilletProps {
    payload: any;
    onBilletGenerated: (billetData: GenerateBilletResponse) => void;
    onBilletErrored: (err: any) => void;
  }
  const customError = 'empty-billet-data';

  const newGenerateBillet = async ({ payload, onBilletGenerated, onBilletErrored }: NewGenerateBilletProps) => {
    const res = await apiGenerateBillet
      .send(payload)
      .then((response) => {
        onBilletGenerated(response);
        if (!response.linhaDigitavel) throw new Error(customError);
        setBillet(response);
        setIsBilletGenerationError(false);

        return response;
      })
      .catch((err) => {
        if (err.message === customError) return;
        setIsBilletGenerationError(true);
        onBilletErrored(err);
        throw err;
      });
    return res;
  };

  const newBilletPolling = async ({ payload, onBilletGenerated, onBilletErrored }: NewGenerateBilletProps) => {
    if (isPolling.current) return;

    const alreadyPolled = localStorage.getItem(constants.debts.BILLET_POLL);
    if (alreadyPolled) {
      if (isMounted.current) setIsPollingBillet(false);
      return;
    }

    isPolling.current = true;
    if (isMounted.current) setIsPollingBillet(true);

    const executePolling = async (count: number) => {
      if (!isMounted.current || !isPolling.current) return;

      try {
        const res = await newGenerateBillet({ payload, onBilletGenerated, onBilletErrored });

        if (!isMounted.current || !isPolling.current) return;

        if (!res?.linhaDigitavel && count < MAX_BILLET_POLLING_ATTEMPTS) {
          setTimeout(() => executePolling(count + 1), POLLING_INTERVAL_MS);
        } else {
          isPolling.current = false;
          localStorage.setItem(constants.debts.BILLET_POLL, 'true');
          if (isMounted.current) setIsPollingBillet(false);
        }
      } catch (error) {
        if (!isMounted.current || !isPolling.current) return;

        if (count < MAX_BILLET_POLLING_ATTEMPTS) {
          setTimeout(() => executePolling(count + 1), POLLING_INTERVAL_MS);
        } else {
          isPolling.current = false;
          localStorage.setItem(constants.debts.BILLET_POLL, 'true');
          if (isMounted.current) setIsPollingBillet(false);
        }
      }
    };

    executePolling(1);
  };

  const handleCopyBilletCode = (codeToCopy: string) => {
    trackCodeCopy(codeToCopy);
    const msReturnDefaultButton = 2000;

    navigator.clipboard
      .writeText(codeToCopy)
      .then(() => {
        trackCodeRetrieved(codeToCopy);
        setBilletBtnTxt('Código do boleto copiado');

        if (refCopiedWarning?.current) {
          refCopiedWarning.current.open({
            visible: true,
            typeCode: 'boleto',
          });
        }

        setTimeout(() => {
          setBilletBtnTxt('Copiar código do boleto');

          if (refCopiedWarning?.current) {
            refCopiedWarning.current.open({
              visible: false,
            });
          }
        }, msReturnDefaultButton);
      })
      .catch((error) => {
        trackRetrievalFailed(codeToCopy, error);
        console.error('[COPY BILLET CODE]:', error);
      });
  };

  const handleBilletDownload = () => {
    const billetLink = billet?.link || caixa.getCaixaExternalBillet(installment);

    const code = billetLink || billet?.pdf;
    trackBilletDownload(code);

    if (code) {
      trackBilletFileRetrieved(code);
    }

    if (billetLink) {
      window.open(billetLink, '_blank');
    } else if (billet?.pdf) {
      openBase64PdfInTab(billet?.pdf);
    } else {
      setLoadingDownloadBillet(true);

      const payload = {
        installmentCode: installment?.installmentCode,
        parameters: {
          partnerSystemIdentifier: agreement?.partner?.identificador,
          requestParameters: {
            dividaId: agreement?.debt?.id,
            opcaoPagamentoId: installment?.id,
          },
        },
      };

      const onBilletGenerated = (response: GenerateBilletResponse) => {
        setLoadingDownloadBillet(false);

        trackBilletFileRetrieved(code);

        setBillet(response);
        setIsBilletGenerationError(false);
        openBase64PdfInTab(response.pdf);
      };

      const onBilletErrored = (err: any) => {
        trackDownloadFailed(code, err);
        setBillet({} as GenerateBilletResponse);
        setIsBilletGenerationError(true);
        setLoadingDownloadBillet(false);

        const billetDownloadError = {
          agreement,
          isMyAgreementsPage,
        };
        localStorage.setItem(constants.debts.BILLET_NOT_FOUND, JSON.stringify(billetDownloadError));
        window.open(pageNames.billetNotFoundError.path, '_blank');
      };

      newGenerateBillet({ payload, onBilletGenerated, onBilletErrored });
    }
  };

  const onFinishFirstBilletReq = (billetData: GenerateBilletResponse) => {
    setBillet(billetData);
    setIsPollingBillet(false);
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      isPolling.current = false;
    };
  }, []);

  return {
    newGenerateBillet,
    newBilletPolling,
    handleCopyBilletCode,
    billetBtnTxt,
    loadingDownloadBillet,
    handleBilletDownload,
    isBilletGenerationError,
    isPollingBillet,
    billet,
    onFinishFirstBilletReq,
  };
};

export default useBilletHandler;
